import React, { useState, useEffect } from 'react';
import { Users, TrendingUp, Crown, Trash2, Edit, Search, RefreshCw, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import NavigationMenu from '../components/NavigationMenu';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [showDownloadProgress, setShowDownloadProgress] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadStep, setDownloadStep] = useState('');
  const [editForm, setEditForm] = useState({ 
    name: '', 
    email: '', 
    password: '',
    daysActive: 0  // NOVO: dias de uso
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/stats`, { withCredentials: true })
      ]);
      
      setUsers(usersRes.data.users);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      if (error.response?.status === 403) {
        toast.error('Acesso negado - apenas administradores');
      } else {
        toast.error('Erro ao carregar dados');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    try {
      await axios.delete(`${API}/admin/users/${selectedUser.id}`, { withCredentials: true });
      toast.success('Usuário deletado com sucesso');
      setShowDeleteDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Erro ao deletar usuário');
    }
  };

  const handleEditUser = async () => {
    try {
      const updateData = {};
      if (editForm.name) updateData.name = editForm.name;
      if (editForm.email) updateData.email = editForm.email;
      if (editForm.password) updateData.password = editForm.password;
      if (editForm.daysActive !== undefined) updateData.days_active = parseInt(editForm.daysActive);

      await axios.put(`${API}/admin/users/${selectedUser.id}`, updateData, { withCredentials: true });
      toast.success('Usuário atualizado');
      setShowEditDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Erro ao atualizar usuário');
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditForm({ 
      name: user.name, 
      email: user.email, 
      password: '',
      daysActive: user.days_active || 0
    });
    setShowEditDialog(true);
  };
  
  const handleChangePlan = async (plan) => {
    try {
      await axios.post(`${API}/admin/users/${selectedUser.id}/change-plan`, 
        { plan }, 
        { withCredentials: true }
      );
      toast.success(`Plano alterado para ${plan}`);
      setShowPlanDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Erro ao alterar plano');
    }
  };
  
  const handleCancelSubscription = async () => {
    try {
      const response = await axios.post(`${API}/admin/users/${selectedUser.id}/cancel-subscription`,
        {},
        { withCredentials: true }
      );
      
      // Toast baseado no resultado
      if (response.data.stripe_cancelled) {
        toast.success('✅ Cancelado no Stripe e no Banco');
      } else if (response.data.stripe_subscription_id) {
        toast.warning('⚠️ Erro no Stripe, mas cancelado no Banco');
      } else {
        toast.info('ℹ️ Cancelado no Banco (usuário sem Stripe)');
      }
      
      setShowPlanDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Erro ao cancelar');
    }
  };

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
        <NavigationMenu />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-orange-600 border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <NavigationMenu />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold text-amber-900 mb-2">🔐 Painel de Administração</h1>
              <p className="text-gray-600">Gerenciamento completo do sistema</p>
            </div>
            <Button
              onClick={() => navigate('/admin/messages')}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <Mail className="w-4 h-4 mr-2" />
              Mensagens ({stats?.users_by_plan?.trial || 0})
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="p-6 border-2 border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total de Usuários</p>
                  <p className="text-3xl font-bold text-blue-600">{stats.total_users}</p>
                </div>
                <Users className="w-12 h-12 text-blue-400" />
              </div>
            </Card>

            <Card className="p-6 border-2 border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Usuários Premium</p>
                  <p className="text-3xl font-bold text-green-600">{stats.users_by_plan.premium}</p>
                </div>
                <Crown className="w-12 h-12 text-green-400" />
              </div>
            </Card>

            <Card className="p-6 border-2 border-orange-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Em Trial</p>
                  <p className="text-3xl font-bold text-orange-600">{stats.users_by_plan.trial}</p>
                </div>
                <TrendingUp className="w-12 h-12 text-orange-400" />
              </div>
            </Card>

            <Card className="p-6 border-2 border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Receitas</p>
                  <p className="text-3xl font-bold text-gray-600">{stats.total_recipes}</p>
                </div>
                <TrendingUp className="w-12 h-12 text-gray-400" />
              </div>
            </Card>
          </div>
        )}

        {/* Search and Actions */}
        <Card className="p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="flex-1 w-full sm:w-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Buscar por nome ou email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={async () => {
                  try {
                    setShowDownloadProgress(true);
                    setDownloadProgress(0);
                    
                    // Simular etapas para dar feedback
                    setDownloadStep('Preparando arquivos...');
                    setDownloadProgress(10);
                    await new Promise(r => setTimeout(r, 500));
                    
                    setDownloadStep('Compactando backend...');
                    setDownloadProgress(30);
                    await new Promise(r => setTimeout(r, 500));
                    
                    setDownloadStep('Compactando frontend...');
                    setDownloadProgress(50);
                    await new Promise(r => setTimeout(r, 500));
                    
                    setDownloadStep('Incluindo projeto iOS...');
                    setDownloadProgress(70);
                    await new Promise(r => setTimeout(r, 500));
                    
                    setDownloadStep('Gerando arquivo ZIP...');
                    setDownloadProgress(90);
                    
                    // Download do código fonte
                    window.location.href = `${API}/admin/download-source`;
                    
                    await new Promise(r => setTimeout(r, 1000));
                    setDownloadProgress(100);
                    setDownloadStep('Download iniciado!');
                    
                    toast.success('Download completo!');
                    
                    setTimeout(() => {
                      setShowDownloadProgress(false);
                      setDownloadProgress(0);
                      setDownloadStep('');
                    }, 2000);
                  } catch (error) {
                    setShowDownloadProgress(false);
                    toast.error('Erro ao baixar');
                  }
                }}
                variant="outline"
                className="border-purple-500 text-purple-600 hover:bg-purple-50"
              >
                📦 Download Código
              </Button>
              <Button 
                onClick={async () => {
                  try {
                    const res = await axios.post(`${API}/admin/fix-orphaned-members`, {}, { withCredentials: true });
                    toast.success(res.data.message);
                    fetchData();
                  } catch (error) {
                    toast.error('Erro ao corrigir membros');
                  }
                }}
                variant="outline"
                className="border-orange-500 text-orange-600 hover:bg-orange-50"
              >
                Corrigir Membros Órfãos
              </Button>
              <Button onClick={fetchData} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />
                Atualizar
              </Button>
            </div>
          </div>
        </Card>

        {/* Users Table */}
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Plano</TableHead>
                <TableHead>Tempo de Uso</TableHead>
                <TableHead>Receitas</TableHead>
                <TableHead>Famílias</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Badge 
                        className={`cursor-pointer hover:opacity-80 ${
                          user.subscription_status === 'premium' ? 'bg-green-600' :
                          user.subscription_status === 'free' ? 'bg-gray-400' :
                          'bg-blue-600'
                        }`}
                        onClick={() => {
                          setSelectedUser(user);
                          setShowPlanDialog(true);
                        }}
                      >
                        {user.subscription_status || 'free'}
                      </Badge>
                      {user.subscription?.status === 'cancelled' && (
                        <Badge variant="destructive" className="text-xs">
                          Cancelado
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{user.days_active || 0} dias</TableCell>
                  <TableCell>{user.recipe_count || 0}</TableCell>
                  <TableCell>{user.families?.length || 0}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditDialog(user)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => {
                          setSelectedUser(user);
                          setShowDeleteDialog(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        {/* Delete Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmar Exclusão</DialogTitle>
              <DialogDescription>
                Tem certeza que deseja deletar o usuário <strong>{selectedUser?.email}</strong>?
                Esta ação não pode ser desfeita e deletará todas as receitas, conversas e dados do usuário.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDeleteUser}>
                Deletar Permanentemente
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Editar Usuário</DialogTitle>
              <DialogDescription>
                Editando: {selectedUser?.email}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Nome</label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Email</label>
                <Input
                  value={editForm.email}
                  onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Nova Senha (deixe vazio para não alterar)</label>
                <Input
                  type="password"
                  value={editForm.password}
                  onChange={(e) => setEditForm({...editForm, password: e.target.value})}
                  placeholder="Digite nova senha..."
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Tempo de Uso (dias)</label>
                <Input
                  type="number"
                  value={editForm.daysActive}
                  onChange={(e) => setEditForm({...editForm, daysActive: e.target.value})}
                  placeholder="Ex: 90 (3 meses)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Ajusta a data de criação do usuário
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleEditUser}>
                Salvar Alterações
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Plan Management Dialog */}
        <Dialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Gerenciar Plano</DialogTitle>
              <DialogDescription>
                Usuário: {selectedUser?.email}
                <br />
                Plano atual: <strong>{selectedUser?.subscription_status || 'free'}</strong>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-4">
              <p className="text-sm text-gray-600 mb-4">Escolha uma ação:</p>
              
              <Button
                onClick={() => handleChangePlan('free')}
                variant="outline"
                className="w-full justify-start border-gray-300"
              >
                🆓 Alterar para Free (apenas banco)
              </Button>
              
              <Button
                onClick={() => handleChangePlan('premium')}
                variant="outline"
                className="w-full justify-start border-green-300 text-green-700 hover:bg-green-50"
              >
                👑 Alterar para Premium (apenas banco)
              </Button>
              
              <Button
                onClick={() => handleChangePlan('family')}
                variant="outline"
                className="w-full justify-start border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                👨‍👩‍👧 Alterar para Family (apenas banco)
              </Button>
              
              <div className="border-t pt-3 mt-3">
                <Button
                  onClick={handleCancelSubscription}
                  variant="destructive"
                  className="w-full"
                >
                  ⚠️ Cancelar Assinatura (Stripe + Banco)
                </Button>
                <p className="text-xs text-red-600 mt-2">
                  Cancela no Stripe e marca como cancelado no sistema
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPlanDialog(false)}>
                Fechar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Download Progress Dialog */}
        <Dialog open={showDownloadProgress} onOpenChange={setShowDownloadProgress}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>📦 Baixando Código Fonte</DialogTitle>
              <DialogDescription>
                Preparando mAI Recipes para download
              </DialogDescription>
            </DialogHeader>
            <div className="py-6">
              <div className="mb-4">
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">{downloadStep}</span>
                  <span className="text-sm font-medium text-gray-700">{downloadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div 
                    className="bg-purple-600 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${downloadProgress}%` }}
                  ></div>
                </div>
              </div>
              
              <div className="space-y-2 text-sm text-gray-600">
                <p>✅ Backend (Python + FastAPI)</p>
                <p>✅ Frontend (React + Components)</p>
                <p>✅ Projeto iOS (Capacitor + Xcode)</p>
                <p>✅ Configurações (package.json, requirements.txt)</p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminDashboard;
